import React, { FC, useEffect, useState } from 'react';

import { SearchableSelectType } from 'components/inputs/searchableSelect';
import RenderHTML from 'components/shared/RenderHTML';
import styled from 'styled-components';

import aampeTheme from '../../../../constants/aampeTheme';

import useCustomEmailTemplateDetail from 'hooks/custom-email-templates/useCustomEmailTemplateDetail';

interface Step1TemplateSelectionProps {
    templateOptions: SearchableSelectType[];
    temporaryValue: SearchableSelectType | undefined;
    setTemporaryValue: (value: SearchableSelectType) => void;
    currentTemplateId: string;
}
const Step1TemplateSelection: FC<Step1TemplateSelectionProps> = ({
    templateOptions,
    temporaryValue,
    currentTemplateId,
}) => {
    const currentTemplate = templateOptions.find(
        (option) => option.value === currentTemplateId,
    );

    const [currentHtmlData, setCurrentHtmlData] = useState<string>('');
    const { data, isPending: isLoading } =
        useCustomEmailTemplateDetail(currentTemplateId);
    const { data: candidateData } = useCustomEmailTemplateDetail(
        (temporaryValue?.value as string) ?? '',
        !!temporaryValue?.value,
    );

    useEffect(() => {
        if (!isLoading && data) {
            setCurrentHtmlData(data.html_content ?? '');
        }
    }, [isLoading, data]);
    return (
        <Container>
            <SideBySideContainer>
                {/* LEFT SIDE - Current Template */}
                <HalfPanel>
                    <PreviewBox>
                        {currentTemplate ? (
                            <>
                                <TemplateInfo>
                                    {currentTemplate.trailingIcon && (
                                        <IconWrapper>
                                            {currentTemplate.trailingIcon}
                                        </IconWrapper>
                                    )}
                                    <TemplateName>
                                        {currentTemplate.label}
                                    </TemplateName>
                                </TemplateInfo>
                                <RenderHTML htmlString={currentHtmlData} />
                            </>
                        ) : (
                            <EmptyState>No current template</EmptyState>
                        )}
                    </PreviewBox>
                </HalfPanel>

                {/* RIGHT SIDE - New Template */}
                <HalfPanel>
                    <PreviewBox>
                        {temporaryValue ? (
                            <>
                                <TemplateInfo>
                                    {temporaryValue.trailingIcon && (
                                        <IconWrapper>
                                            {temporaryValue.trailingIcon}
                                        </IconWrapper>
                                    )}
                                    <TemplateName>
                                        {temporaryValue.label}
                                    </TemplateName>
                                </TemplateInfo>
                                <RenderHTML
                                    htmlString={
                                        candidateData?.html_content ?? ''
                                    }
                                ></RenderHTML>
                            </>
                        ) : (
                            <EmptyState>
                                <EmptyStateText>
                                    Choose an email template from the dropdown
                                    above to map all selected message groups to
                                    it.
                                </EmptyStateText>
                            </EmptyState>
                        )}
                    </PreviewBox>
                </HalfPanel>
            </SideBySideContainer>
        </Container>
    );
};

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(4),
    height: '100%',
});

const SideBySideContainer = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing4(4),
    alignItems: 'stretch',
    flex: 1,
    minHeight: 0,
});

const HalfPanel = styled.div({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(3),
    minWidth: 0,
});

const PreviewBox = styled.div({
    flex: 1,
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    padding: aampeTheme.spacing4(4),
    backgroundColor: aampeTheme.colors.white,
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(3),
    overflow: 'auto',
});

const TemplateInfo = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
    paddingBottom: aampeTheme.spacing4(3),
    borderBottom: `1px solid ${aampeTheme.colors.interactiveWG01}`,
});

const IconWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const TemplateName = styled.span({
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.light,
    color: aampeTheme.colors.gray800,
});

const EmptyState = styled.div({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: aampeTheme.spacing4(6),
});

const EmptyStateText = styled.p({
    fontSize: aampeTheme.size.font.default,
    color: aampeTheme.colors.textAccessibleWG01,
    textAlign: 'center',
    maxWidth: '400px',
    lineHeight: aampeTheme.size.lineHeight.default,
    margin: 0,
});

export default Step1TemplateSelection;
